import { useCallback, useMemo, useState } from 'react'

import type {
	ExplorerFilter,
	ExplorerReport,
	ExplorerReportDefinition,
	ExplorerReportScope,
	ExplorerSort,
} from '../types/explorer.types'

/**
 * The live, editable report definition (MYD-359).
 *
 * The grid always renders `state`. `loadedReport` is whichever saved report the
 * state came from, and `dirty` tracks divergence from it, which is what lets
 * the toolbar know whether Save applies or only Save As.
 *
 * A definition belongs to exactly one grain, so the hook is told which grain it
 * is holding and empties itself when that changes. See `useExplorerDefinition`
 * for why that reset cannot be an effect.
 */
export interface ExplorerDefinitionState {
	columns: string[]
	filters: ExplorerFilter[]
	sorts: ExplorerSort[]
	groupBy: string | null
	/** Column widths in pixels. A column with no entry sizes itself. */
	widths: Record<string, number>
	/**
	 * The one asset the report is narrowed to (MYD-598), or null. Part of the
	 * definition, so clearing it marks the report as changed.
	 */
	scope: ExplorerReportScope | null
}

const EMPTY: ExplorerDefinitionState = {
	columns: [],
	filters: [],
	sorts: [],
	groupBy: null,
	widths: {},
	scope: null,
}

const fromDefinition = (source: ExplorerReportDefinition | undefined): ExplorerDefinitionState => {
	const definition = source ?? ({ columns: [] } as unknown as ExplorerReportDefinition)
	return {
		columns: definition.columns ?? [],
		filters: definition.filters ?? [],
		sorts: definition.sorts ?? [],
		groupBy: definition.groupBy ?? null,
		widths: definition.widths ?? {},
		// Only a scope with an id narrows anything; a template's id-less one does not.
		scope: definition.scope?.id ? { type: definition.scope.type, id: definition.scope.id } : null,
	}
}

const fromReport = (report: ExplorerReport): ExplorerDefinitionState => fromDefinition(report.definition)

export interface UseExplorerDefinition {
	state: ExplorerDefinitionState
	loadedReport: ExplorerReport | null
	dirty: boolean
	loadReport: (report: ExplorerReport) => void
	/** Open an unsaved definition, e.g. one made from a template. */
	loadDraft: (definition: ExplorerReportDefinition) => void
	startBlank: (columns: string[], sorts?: ExplorerSort[]) => void
	markSaved: (report: ExplorerReport) => void
	setColumns: (columns: string[]) => void
	setFilters: (filters: ExplorerFilter[]) => void
	setSorts: (sorts: ExplorerSort[]) => void
	setGroupBy: (groupBy: string | null) => void
	setColumnWidth: (key: string, width: number | null) => void
	/** Narrow to one asset, or `null` to widen back to everything visible. */
	setScope: (scope: ExplorerReportScope | null) => void
	toggleSort: (key: string) => void
	toDefinition: () => ExplorerReportDefinition
}

/**
 * @param grainKey the grain this definition describes. Changing it empties the
 *   definition, so the screen reseeds from the new grain's own defaults.
 */
export function useExplorerDefinition(grainKey?: string | null): UseExplorerDefinition {
	const [state, setState] = useState<ExplorerDefinitionState>(EMPTY)
	const [loadedReport, setLoadedReport] = useState<ExplorerReport | null>(null)
	const [baseline, setBaseline] = useState<string>(JSON.stringify(EMPTY))
	const [heldGrain, setHeldGrain] = useState<string | null | undefined>(grainKey)

	// Columns, filters, sorts and grouping are all keyed to one grain's catalog,
	// and the Explorer screen stays mounted as the tabs change the grain in the
	// URL. Without this, a tab switch carried the previous grain's definition
	// across: the defaults for the new grain were never seeded (the column list
	// was not empty), the loaded report stayed open so Save would write one
	// grain's layout over another grain's report, and the keys that exist under
	// both grains (location.assetName is in the Sites catalog too) passed
	// server-side validation and returned rows modelled on the tab just left.
	//
	// Adjusted during render rather than in an effect on purpose: an effect runs
	// after commit, so one render would still fetch and paint with the previous
	// grain's definition before the reset landed. React re-runs this component
	// immediately instead, and nothing stale reaches the screen.
	if (grainKey !== heldGrain) {
		setHeldGrain(grainKey)
		setState(EMPTY)
		setLoadedReport(null)
		setBaseline(JSON.stringify(EMPTY))
	}

	const dirty = useMemo(() => JSON.stringify(state) !== baseline, [state, baseline])

	const loadReport = useCallback((report: ExplorerReport) => {
		const next = fromReport(report)
		setState(next)
		setLoadedReport(report)
		setBaseline(JSON.stringify(next))
	}, [])

	/**
	 * Open a definition that is not a saved report: a template made concrete for
	 * one asset (MYD-598). Nothing is loaded, so the picker offers "Save report"
	 * and the draft is kept only if the user saves it.
	 */
	const loadDraft = useCallback((definition: ExplorerReportDefinition) => {
		const next = fromDefinition(definition)
		setState(next)
		setLoadedReport(null)
		setBaseline(JSON.stringify(next))
	}, [])

	/**
	 * Start from a grain's defaults rather than an empty grid.
	 *
	 * Not marked dirty: the user has not chosen anything yet, and offering
	 * "Save" over a layout they never assembled is noise.
	 */
	const startBlank = useCallback((columns: string[], sorts: ExplorerSort[] = []) => {
		const next: ExplorerDefinitionState = {
			columns,
			filters: [],
			sorts,
			groupBy: null,
			widths: {},
			scope: null,
		}
		setState(next)
		setLoadedReport(null)
		setBaseline(JSON.stringify(next))
	}, [])

	/**
	 * Reset the baseline from the PERSISTED report, not from local state.
	 *
	 * Using local state would mark an edit made while the save was in flight as
	 * clean, and that edit would then be silently lost.
	 */
	const markSaved = useCallback((report: ExplorerReport) => {
		setLoadedReport(report)
		setBaseline(JSON.stringify(fromReport(report)))
	}, [])

	const setColumns = useCallback((columns: string[]) => {
		setState(prev => ({
			...prev,
			columns,
			// Sorts and grouping require a SELECTED column, so drop any that
			// reference a removed one or the next run 400s.
			//
			// Filters are deliberately KEPT. The engine filters against the full
			// catalog even for columns that are not displayed, so dropping them
			// here would silently change which rows come back the moment someone
			// hid a column, and lose a filter they had deliberately set.
			sorts: prev.sorts.filter(sort => columns.includes(sort.key)),
			groupBy: prev.groupBy && columns.includes(prev.groupBy) ? prev.groupBy : null,
		}))
	}, [])

	const setFilters = useCallback((filters: ExplorerFilter[]) => {
		setState(prev => ({ ...prev, filters }))
	}, [])

	const setSorts = useCallback((sorts: ExplorerSort[]) => {
		setState(prev => ({ ...prev, sorts }))
	}, [])

	const setGroupBy = useCallback((groupBy: string | null) => {
		setState(prev => ({ ...prev, groupBy }))
	}, [])

	/**
	 * Set or clear one column width.
	 *
	 * `null` REMOVES the entry rather than storing a zero, so the column goes
	 * back to sizing itself. Storing a width of 0 would pin it shut.
	 */
	const setColumnWidth = useCallback((key: string, width: number | null) => {
		setState(prev => {
			const widths = { ...prev.widths }
			if (width === null) delete widths[key]
			else widths[key] = width
			return { ...prev, widths }
		})
	}, [])

	const setScope = useCallback((scope: ExplorerReportScope | null) => {
		setState(prev => ({ ...prev, scope }))
	}, [])

	/**
	 * Header-click sorting: ascending, then descending, then off.
	 *
	 * Single-column, replacing any existing sort. A grid that silently
	 * accumulates sort keys as you click around becomes impossible to reason
	 * about, and the third click has to clear rather than cycle back to
	 * ascending or there is no way to remove a sort by clicking.
	 */
	const toggleSort = useCallback((key: string) => {
		setState(prev => {
			const current = prev.sorts.find(sort => sort.key === key)
			if (!current) return { ...prev, sorts: [{ key, direction: 'asc' }] }
			if (current.direction === 'asc') return { ...prev, sorts: [{ key, direction: 'desc' }] }
			return { ...prev, sorts: [] }
		})
	}, [])

	/**
	 * The definition to persist.
	 *
	 * `labels` and `starterKey` are carried forward from the loaded report
	 * rather than rebuilt away. `starterKey` is also re-stamped server-side from
	 * the stored row, so this is defence in depth rather than the only guard.
	 */
	const toDefinition = useCallback((): ExplorerReportDefinition => {
		const loaded = loadedReport?.definition
		return {
			columns: state.columns,
			filters: state.filters,
			sorts: state.sorts,
			groupBy: state.groupBy,
			widths: state.widths,
			// Always sent, null included: the server keeps a stored scope when the
			// field is ABSENT, so an explicit null is how removing the chip saves.
			scope: state.scope,
			...(loaded?.labels ? { labels: loaded.labels } : {}),
			...(loaded?.starterKey ? { starterKey: loaded.starterKey } : {}),
		}
	}, [state, loadedReport])

	/**
	 * Memoised so the returned object only changes identity when something
	 * actually changed.
	 *
	 * This is load-bearing, not tidiness. A fresh object every render forces
	 * consumers to either list it in every dependency array (rebuilding each
	 * callback constantly) or omit it, and omitting it captures a STALE
	 * definition: a handler created on the first render would still be holding
	 * the empty column list long after the user had built a report, and would
	 * silently save nothing. With a stable identity, consumers can depend on it
	 * honestly.
	 */
	return useMemo(
		() => ({
			state,
			loadedReport,
			dirty,
			loadReport,
			loadDraft,
			startBlank,
			markSaved,
			setColumns,
			setFilters,
			setSorts,
			setGroupBy,
			setColumnWidth,
			setScope,
			toggleSort,
			toDefinition,
		}),
		[
			state,
			loadedReport,
			dirty,
			loadReport,
			loadDraft,
			startBlank,
			markSaved,
			setColumns,
			setFilters,
			setSorts,
			setGroupBy,
			setColumnWidth,
			setScope,
			toggleSort,
			toDefinition,
		],
	)
}

export default useExplorerDefinition
